import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import type { OptionTranslationsByLanguage } from "@/services/option-translation-service";

dayjs.extend(isoWeek);

export interface IOptionData {
  id: number;
  type_id: number;
  type_name: string;
  type_code?: string;
  type_category?: string;
  type_identifier?: string;
  option_type_identifier?: string;
  long_name?: string;
  translations_by_language?: OptionTranslationsByLanguage | null;
  type_icon: string;
  collection_id: number;
  building_id: string;
  unit_type_id: string;
  language: string;
  name: string;
  description: string;
  price: number;
  price_per_unit_of: string;
  price_per_interval_of: string;
  created_at: string;
  permission: string;

  is_locked: boolean;
  is_selected: boolean;
  is_selected_initially: boolean;
  valid_dates: string[];
  selected_dates: string[];
  is_cleaning_supply?: boolean;
}

export default class OptionData {
  private readonly _isFree: boolean;
  private readonly _isMandatory: boolean;

  public id: number;
  public typeId: number;
  public typeName: string;
  public typeCode: string;
  public typeCategory: string;
  public longName: string;
  public translationsByLanguage: OptionTranslationsByLanguage | null;
  public typeIcon: string;
  public collectionId: number;
  public buildingId: string;
  public unitTypeId: string;
  public language: string;
  public name: string;
  public description: string;
  public price: number;
  public pricePerUnitOf: string;
  public pricePerIntervalOf: string;
  public createdAt: string;
  public permission: string;

  public isLocked: boolean;
  public isSelected: boolean;
  public isSelectedInitially: boolean;
  public validDates: string[];
  public selectedDates: string[];
  public isCleaningSupply?: boolean;

  constructor(data: IOptionData) {
    this.id = data.id;
    this.typeId = data.type_id;
    this.typeName = data.type_name;
    this.typeCode = data.type_code ?? data.type_identifier ?? data.option_type_identifier ?? "";
    this.typeCategory = data.type_category ?? "";
    this.longName = data.long_name ?? "";
    this.translationsByLanguage = data.translations_by_language ?? null;
    this.typeIcon = data.type_icon;
    this.collectionId = data.collection_id;
    this.buildingId = data.building_id;
    this.unitTypeId = data.unit_type_id;
    this.language = data.language;
    this.name = data.name;
    this.description = data.description;
    this.price = data.price;
    this.pricePerUnitOf = data.price_per_unit_of;
    this.pricePerIntervalOf = data.price_per_interval_of;
    this.createdAt = data.created_at;
    this.permission = data.permission;

    this.isLocked = data.is_locked ? data.is_locked : false;
    this.isSelected = data.is_selected ? data.is_selected : false;
    this.isSelectedInitially = data.is_selected_initially ? data.is_selected_initially : false;
    this.validDates = data.valid_dates ?? [];
    this.selectedDates = data.selected_dates ?? [];
    this.isCleaningSupply = data.is_cleaning_supply;


    this._isFree = this.price <= 0;
    this._isMandatory = this.permission === "mandatory";
  }

  public get iconClass(): string {
    return this.iconClassForType(this.typeId);
  }

  public get icon(): string {
    return this.iconForType(this.typeId);
  }

  public get shortDescription(): string {
    const d = (this.description ?? "").trim();
    if (!d) return "";

    const idx = d.indexOf(".");
    return idx === -1 ? d : d.slice(0, idx + 1).trim();
  }

  toDTO(): IOptionData {
    return {
      id: this.id,
      type_id: this.typeId,
      type_name: this.typeName,
      type_code: this.typeCode,
      type_category: this.typeCategory,
      long_name: this.longName,
      translations_by_language: this.translationsByLanguage,
      type_icon: this.typeIcon,
      collection_id: this.collectionId,
      building_id: this.buildingId,
      unit_type_id: this.unitTypeId,
      language: this.language,
      name: this.name,
      description: this.description,
      price: this.price,
      price_per_unit_of: this.pricePerUnitOf,
      price_per_interval_of: this.pricePerIntervalOf,
      created_at: this.createdAt,
      permission: this.permission,
      is_locked: this.isLocked,
      is_selected: this.isSelected,
      is_selected_initially: this.isSelectedInitially,
      valid_dates: this.validDates,
      selected_dates: this.selectedDates,
      is_cleaning_supply: this.isCleaningSupply
    };
  }

  public toggleDate(date: string): void {
    const index = this.selectedDates.indexOf(date);
    if (index > -1) {
      this.selectedDates.splice(index, 1);
    } else {
      this.selectedDates.push(date);
    }

    this.isSelected = this.selectedDates.length > 0;
  }

  public toggleWeekday(dayIndex: number): void {
    const allDatesForDay = this.validDates.filter(date => {
      return dayjs(date).isoWeekday() === dayIndex + 1;
    });

    const allSelected = allDatesForDay.every(date => this.selectedDates.includes(date));

    if (allSelected) {
      this.selectedDates = this.selectedDates.filter(date => !allDatesForDay.includes(date));
    } else {
      const newDates = [...this.selectedDates];
      allDatesForDay.forEach(date => {
        if (!newDates.includes(date)) {
          newDates.push(date);
        }
      });
      this.selectedDates = newDates;
    }

    this.isSelected = this.selectedDates.length > 0;
  }

  public getWeekdaySelectionStatus(dayIndex: number): "none" | "some" | "all" {
    const allDatesForDay = this.validDates.filter(date => {
      return dayjs(date).isoWeekday() === dayIndex + 1;
    });

    const selectedCount = allDatesForDay.filter(date => this.selectedDates.includes(date)).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === allDatesForDay.length) return "all";
    return "some";
  }

  public selectAllDates(): void {
    this.selectedDates = [...this.validDates];
    this.isSelected = this.selectedDates.length > 0;
  }


  public calculatePrice(nights: number, guests: number, maxOneMonth = false): number {
    if (guests < 1) guests = 1;

    if (this.isBreakfast()) {
      const days = this.selectedDates.length;
      if (days === 0) return 0;

      return this.price * guests * days;
    }

    if (this.isDailyCleaning() && nights > 1){
      nights -=1;
    }

    let price = this.price;

    if (maxOneMonth && this.pricePerIntervalOf === "day") {
      nights = nights > 30 ? 30 : nights;
      price *= nights;
    }
    else if (this.pricePerIntervalOf === "day") price *= nights;
    else if (this.pricePerIntervalOf === "month") price *= Math.round(nights / 30);

    if (this.pricePerUnitOf === "guest") price *= guests;

    return price;
  }

  public calculatePricePerMonth(nights: number, guests: number): number {
    if (nights > 30) {
      nights = 30;
    }
    return this.calculatePrice(nights, guests);
  }

  public calculatePriceReversed(nights: number, guests: number, price: number): number {
    if (guests < 1) guests = 1;

    if (this.pricePerIntervalOf === "day") price /= nights;
    else if (this.pricePerIntervalOf === "month") price /= Math.round(nights / 30);

    if (this.pricePerUnitOf === "guest") price /= guests;

    return price;
  }

  public isFree(): boolean {
    return this._isFree;
  }

  public isMandatory(): boolean {
    return this._isMandatory;
  }

  public isBreakfast(): boolean {
    const breakfastTypeIds: number[] = [8, 46, 48, 54, 67];
    return breakfastTypeIds.includes(this.typeId);
  }

  public isDailyCleaning(): boolean {
    return this.typeId === 15;
  }



  private iconForType(typeId: number): string {
    const m: Record<number, string> = {
      1: "mdi-shield-lock",
      4: "mdi-garage",
      7: "mdi-baby",
      8: "mdi-bag-personal",
      9: "mdi-wifi-arrow-up",
      10: "mdi-wifi",
      11: "mdi-package-variant",
      12: "mdi-package-variant-closed",
      13: "mdi-broom",
      14: "mdi-broom",
      15: "mdi-broom",
      16: "mdi-bed",
      17: "mdi-clock-plus-outline",
      18: "mdi-clock-plus-outline",
      21: "mdi-paw",
      22: "mdi-clock-plus-outline",
      23: "mdi-bed-outline",
      24: "mdi-broom",
      26: "mdi-lightning-bolt",
      28: "mdi-sauna",
      29: "mdi-parking",
      32: "mdi-broom",
      33: "mdi-food",
      34: "mdi-food-turkey",
      35: "mdi-food-sandwich",
      36: "mdi-silverware-fork-knife",
      37: "mdi-account-group",
      38: "mdi-wifi-arrow-up",
      39: "mdi-wifi-arrow-up",
      40: "mdi-wifi-arrow-up",
      41: "mdi-wifi-arrow-up",
      42: "mdi-city",
      43: "mdi-bank-transfer",
      44: "mdi-bike",
      45: "mdi-food",
      46: "mdi-silverware-fork-knife",
      47: "mdi-broom",
      48: "mdi-baby-face-outline",
      49: "mdi-office-building-cog",
      50: "mdi-fire",
      51: "mdi-delete",
      52: "mdi-bike",
      53: "mdi-bike",
      54: "mdi-silverware-fork-knife",
      55: "mdi-glass-flute",
      56: "mdi-glass-flute",
      57: "mdi-heart",
      58: "mdi-hand-heart",
      59: "mdi-spray-bottle",
      60: "mdi-calendar-clock",
      61: "mdi-bucket",
      62: "mdi-calendar-month",
      63: "mdi-file-document-edit",
      64: "mdi-washing-machine",
      65: "mdi-tumble-dryer",
      66: "mdi-broom",
      67: "mdi-silverware-fork-knife",
      68: "mdi-tag",
      69: "mdi-key",
      70: "mdi-lock-reset",
      71: "mdi-bed"
    };

    return m[typeId] ?? "mdi-plus-circle-outline";
  }

  private iconClassForType(typeId: number): string {
    const m: Record<number, string> = {
      // blue-ish: parking/garage/city/building-ish
      4: "option-icon--blue",
      28: "option-icon--blue",
      29: "option-icon--blue",
      42: "option-icon--blue",
      49: "option-icon--blue",

      // green: bikes / eco / garden-ish
      44: "option-icon--green",
      52: "option-icon--green",
      53: "option-icon--green",
      57: "option-icon--green",
      58: "option-icon--green",

      // purple: time/bed/late/upgrade-ish
      16: "option-icon--purple",
      17: "option-icon--purple",
      18: "option-icon--purple",
      22: "option-icon--purple",
      23: "option-icon--purple",
      60: "option-icon--purple",
      62: "option-icon--purple",
      71: "option-icon--purple",

      // orange: food/breakfast
      33: "option-icon--orange",
      34: "option-icon--orange",
      35: "option-icon--orange",
      36: "option-icon--orange",
      45: "option-icon--orange",
      46: "option-icon--orange",
      54: "option-icon--orange",
      67: "option-icon--orange",

      // teal: wifi/tech/utilities
      9: "option-icon--teal",
      10: "option-icon--teal",
      38: "option-icon--teal",
      39: "option-icon--teal",
      40: "option-icon--teal",
      41: "option-icon--teal",
      26: "option-icon--teal",
      64: "option-icon--teal",
      65: "option-icon--teal",

      // indigo: cleaning/housekeeping/services
      13: "option-icon--indigo",
      14: "option-icon--indigo",
      15: "option-icon--indigo",
      24: "option-icon--indigo",
      32: "option-icon--indigo",
      47: "option-icon--indigo",
      59: "option-icon--indigo",
      61: "option-icon--indigo",
      66: "option-icon--indigo",

      // pink: people/groups/kids/pets
      7: "option-icon--pink",
      8: "option-icon--pink",
      21: "option-icon--pink",
      37: "option-icon--pink",
      48: "option-icon--pink",

      // red: warnings/destructive/fire/security/payments
      1: "option-icon--red",
      43: "option-icon--red",
      50: "option-icon--red",
      51: "option-icon--red",
      70: "option-icon--red",
      69: "option-icon--red",

      // gray-ish misc: packages/tags/docs
      11: "option-icon--gray",
      12: "option-icon--gray",
      63: "option-icon--gray",
      68: "option-icon--gray"
    };

    return m[typeId] ?? "option-icon--gray";
  }
}
